import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, LogIn } from 'lucide-react';

const navItems = ['Platform', 'Solutions', 'Agents', 'Integrations'];

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <motion.nav
      data-testid="navbar"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="fixed top-0 left-0 right-0 z-50 px-4 sm:px-6 lg:px-12 py-4"
    >
      <div className="flex items-center justify-between">
        {/* Left - Grow With Us (hidden on mobile) */}
        <motion.span
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
          className="text-xs sm:text-sm font-medium text-emerald-400 tracking-wider hidden md:block"
        >
          GROW WITH US
        </motion.span>

        {/* Mobile - Logo on left */}
        <motion.a
          href="/"
          data-testid="navbar-logo"
          className="flex md:hidden items-center gap-2"
          whileHover={{ scale: 1.02 }}
        >
          <img 
            src="/vritta-logo.png" 
            alt="Vrittaa AI Logo" 
            className="w-8 h-8 object-contain"
          />
          <span className="text-lg font-bold font-outfit tracking-wider text-white uppercase">
            VRITTAA
          </span>
        </motion.a>

        {/* Center - Logo (desktop only) */}
        <motion.a
          href="/"
          data-testid="navbar-logo-desktop"
          className="hidden md:flex flex-col items-center gap-1"
          whileHover={{ scale: 1.02 }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="flex items-center gap-2">
            <img 
              src="/vritta-logo.png" 
              alt="Vrittaa AI Logo" 
              className="w-10 h-10 object-contain"
            />
            <span className="text-xl font-bold font-outfit tracking-[0.3em] text-white uppercase">
              VRITTAA
            </span>
          </div>
          <span className="text-[10px] tracking-[0.4em] text-emerald-400 uppercase">
            AI Agency
          </span>
        </motion.a>

        {/* Right - Login & Menu Button */}
        <div className="flex items-center gap-3">
          {/* Login Button - visible on desktop */}
          <Link 
            to="/login"
            className="hidden sm:flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
            data-testid="login-btn"
          >
            <LogIn className="w-4 h-4" />
            Login
          </Link>
          
          {/* Get Started Button - visible on desktop */}
          <Link 
            to="/signup"
            className="hidden sm:block px-5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-full transition-colors"
            data-testid="signup-btn"
          >
            Get Started
          </Link>

          <motion.button
            data-testid="mobile-menu-toggle"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="p-2 text-slate-400 hover:text-white transition-colors sm:hidden"
          >
            {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </motion.button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 bg-slate-950/95 backdrop-blur-lg border-b border-slate-800 mt-2"
          >
            <div className="px-6 py-6 space-y-4">
              {navItems.map((item, index) => (
                <motion.a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="block text-lg text-slate-300 hover:text-white transition-colors py-2"
                  onClick={() => setIsMenuOpen(false)}
                >
                  {item}
                </motion.a>
              ))}
              <div className="pt-4 border-t border-slate-800 space-y-3">
                <Link 
                  to="/login" 
                  className="block w-full py-3 text-center text-slate-300 hover:text-white border border-slate-700 rounded-full font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Login
                </Link>
                <Link 
                  to="/signup" 
                  className="block w-full py-3 text-center bg-emerald-600 hover:bg-emerald-500 text-white rounded-full font-medium transition-colors"
                  onClick={() => setIsMenuOpen(false)}
                >
                  Get Started
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
